import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { FileText, ScanText, Search } from 'lucide-react';
import { api } from '../../api';
import { pode, useSessao } from '../../sessao';
import { NOME_STATUS_CONTRATO, NOME_TIPO_CONTRATO, dataBr, dinheiro } from './comum';

interface Item {
  id: string; numero: string; tipo: string; status: string; origem: string; inicio: string; fim: string | null; janela: string;
  valor_mensal: string | null; cliente_id: string; cliente: string; sistemas: number; sla_critica_min: number | null;
}

export function ListaContratos() {
  const { usuario } = useSessao();
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();
  const [busca, setBusca] = useState(params.get('busca') ?? '');
  const [lista, setLista] = useState<Item[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const filtro = params.get('filtro') ?? 'vigentes';
  const verValor = pode(usuario, 'contratos.valor');
  const [aConferir, setAConferir] = useState(0);
  useEffect(() => { api<{ resumo: Record<string, number> }>('/contratos/documentos?situacao=pendentes').then((r) => setAConferir(r.resumo.lido ?? 0)).catch(() => {}); }, []);

  useEffect(() => {
    const q = new URLSearchParams();
    if (filtro === 'vigentes') q.set('status', 'vigentes'); else if (filtro === 'a_classificar') q.set('tipo', 'a_classificar'); else if (filtro === 'encerrados') q.set('status', 'encerrado');
    if (params.get('busca')) q.set('busca', params.get('busca')!);
    api<Item[]>(`/contratos?${q}`).then(setLista).catch((e) => setErro(e.message));
  }, [params, filtro]);
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get('busca') ?? '') === busca) return;
      const p = new URLSearchParams(params); busca ? p.set('busca', busca) : p.delete('busca'); setParams(p, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [busca]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="cabecalho-pagina">
        <div><span className="kicker">Comercial</span><h1 className="titulo">Contratos</h1></div>
        <div className="acoes-cabecalho">
          <Link className="btn-fantasma" to="/contratos/documentos"><ScanText size={16} aria-hidden /> Contratos escaneados{aConferir ? <span className="contador">{aConferir}</span> : null}</Link>
          <Link className="btn-fantasma" to="/preventivas">Preventivas do mês</Link>
        </div>
      </div>
      <p className="nota">Contratos nascem no Conta Azul e chegam aqui em até 30 min como "a classificar". Aqui se preenche o Anexo I (tipo, SLA, sistemas cobertos, preventiva).</p>
      <div className="barra-filtros">
        <label className="campo-busca">
          <Search size={18} strokeWidth={1.7} aria-hidden />
          <input type="search" placeholder="Cliente ou número" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar contratos" />
        </label>
        <div className="segmentos" role="group" aria-label="Filtro">
          {[['vigentes', 'Ativos'], ['a_classificar', 'A classificar'], ['encerrados', 'Encerrados'], ['todos', 'Todos']].map(([v, r]) => (
            <button key={v} className={filtro === v ? 'ativo' : ''} onClick={() => { const p = new URLSearchParams(params); p.set('filtro', v!); setParams(p); }}>{r}</button>
          ))}
        </div>
      </div>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      {lista && lista.length === 0 && <div className="cartao vazio"><div className="icone-tile"><FileText size={24} strokeWidth={1.7} aria-hidden /></div><p>Nenhum contrato com esse filtro.</p></div>}
      {lista && lista.length > 0 && (
        <div className="cartao tabela-cartao rolagem-x">
          <table className="tabela tabela-usuarios">
            <thead><tr><th>Cliente</th><th>Tipo</th><th>Vigência</th><th>SLA urgência</th><th className="num">Sistemas</th>{verValor && <th className="num">Valor/mês</th>}<th>Situação</th></tr></thead>
            <tbody>
              {lista.map((k) => (
                <tr key={k.id} onClick={() => navegar(`/contratos/${k.id}`)}>
                  <td><Link to={`/contratos/${k.id}`} onClick={(e) => e.stopPropagation()}><strong>{k.cliente}</strong></Link><small>{k.numero}{k.origem === 'conta_azul' ? ' · Conta Azul' : ''}</small></td>
                  <td>{k.tipo === 'a_classificar' ? <span className="situacao s-aviso">A classificar</span> : NOME_TIPO_CONTRATO[k.tipo]}</td>
                  <td>{dataBr(k.inicio)} → {k.fim ? dataBr(k.fim) : 'indeterminado'}</td>
                  <td>{k.sla_critica_min ? `${k.sla_critica_min / 60} h · ${k.janela === '24h' ? '24 h' : 'comercial'}` : '—'}</td>
                  <td className="num">{k.sistemas || '—'}</td>
                  {verValor && <td className="num">{dinheiro(k.valor_mensal)}</td>}
                  <td><span className={`situacao ${k.status === 'ativo' ? 's-ok' : k.status === 'suspenso' ? 's-nok' : 's-na'}`}>{NOME_STATUS_CONTRATO[k.status]}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <footer className="rodape-tabela"><span>{lista.length} contrato{lista.length === 1 ? '' : 's'}</span></footer>
        </div>
      )}
    </>
  );
}
